import { gsap } from "gsap";
import { Container, Graphics, Sprite, Texture } from "pixi.js";

import { EventsManager } from "../../../../abstracts/events/EventsManager";
import { generateScaledSprite } from "../../../../abstracts/utils/GraphicUtils";
import { playSound } from "../../../../abstracts/utils/SoundUtils";
import { ApocalypseSoundNames } from "../../../assetsLoader/ApocalypseAssetsNames";
import { reelsConfig } from "../../../config/ApocalypseConfig";
import {
    ADD_FAKE_SCATTER,
    REMOVE_FAKE_SCATTERS,
    RESET_FAKE_SCATTERS,
    SHAKE_NORMAL_SCREEN,
    UPDATE_FAKE_SCATTER_POSITION,
} from "../../../events/ApocalypseEvents";
import { DisplayObjectNames } from "../../../types/ApocalypseEnums";
import { ApocalypseFakeScatterData } from "../../../types/ApocalypseInterfaces";
import { shake } from "../../../utils/ApocalypseGraphicUtils";
import { ApocalypseScatterSymbol } from "../symbols/ApocalypseScatterSymbol";
import ApocalypseReel from "./ApocalypseReel";

export class ApocalypseNormalContainer extends Container {
    private topStrip: Sprite;
    private bottomStrip: Sprite;
    protected reelsContainerMask: Container;
    protected background: Sprite;

    public reels: ApocalypseReel[];
    public reelsContainer: Container;
    public fakeScatters: ApocalypseScatterSymbol[];

    constructor(name: string = `ApocalypseNormalContainer`) {
        super();

        this.name = name;
        this.position.set(reelsConfig.reelsContainerX, reelsConfig.reelsContainerY);

        this.init();
    }

    protected init() {
        this.initBackground();
        this.initializeReelsContainer();
        this.initializeMask();
        this.initExtra();
        this.initEvents();
    }

    protected initEvents() {
        EventsManager.on(SHAKE_NORMAL_SCREEN, (intensity: number) => {
            shake(this, intensity);
            playSound({ name: ApocalypseSoundNames.SHAKE_SCREEN });
        });

        EventsManager.on(ADD_FAKE_SCATTER, (scatterData: ApocalypseFakeScatterData) => {
            const scatterSymbol = this.fakeScatters[scatterData.reelIndex];
            scatterSymbol.alpha = 0;

            gsap.to(scatterSymbol, {
                alpha: 1,
                duration: 0.06,
                onStart: () => {
                    this.addChild(scatterSymbol);
                },
            });
        });

        EventsManager.on(UPDATE_FAKE_SCATTER_POSITION, (scatterData: ApocalypseFakeScatterData) => {
            const globalPosition = scatterData.symbolGlobalPosition!;
            const localPosition = this.toLocal(globalPosition);
            const scatterSymbol = this.fakeScatters[scatterData.reelIndex];
            scatterSymbol.position.set(localPosition.x, localPosition.y);
        });

        EventsManager.on(REMOVE_FAKE_SCATTERS, () => {
            this.fakeScatters.map((scatter) => {
                gsap.to(scatter, {
                    alpha: 0,
                    duration: 0.1,
                    onComplete: () => {
                        this.removeChild(scatter);
                        scatter.alpha = 1;
                    },
                });
            });
        });

        EventsManager.on(RESET_FAKE_SCATTERS, () => {
            this.fakeScatters.map((scatter) => {
                const initialPosition = { x: scatter.x, y: scatter.y };
                scatter.reset();
                scatter.position.set(initialPosition.x, initialPosition.y);
            });
        });
    }

    protected initBackground() {
        this.background = new Sprite(Texture.from(`apocalypse/normalReelsBg`));
        this.background.name = `reelsBg`;
        this.background.x = -51;
        this.background.y = -46;
        this.background.scale.y = 0.7;
        this.background.alpha = 0.76;
        this.addChild(this.background);

        this.addBgFrame();
    }

    protected addBgFrame() {
        let backgroundFrame = generateScaledSprite(`apocalypse/frame_base`);
        backgroundFrame.name = `backgroundFrame`;
        backgroundFrame.x = -14;
        backgroundFrame.y = -32;
        backgroundFrame.width = 667;
        backgroundFrame.height = 486;

        this.addChild(backgroundFrame);
    }

    protected initializeReelsContainer(): void {
        this.reelsContainer = new Container();
        this.reelsContainer.name = DisplayObjectNames.REELS_CONTAINER;
        this.addChild(this.reelsContainer);
    }

    protected initializeMask(): void {
        this.reelsContainerMask = new Container();
        this.reelsContainerMask.name = `reelsContainerMask`;

        const containerMaskGr: Graphics = new Graphics();
        containerMaskGr.beginFill(0x00ff00, 0.5);
        containerMaskGr.drawRect(0, 0, this.maskDefaultWidth, this.maskDefaultHeight);
        containerMaskGr.endFill();

        this.reelsContainerMask.addChild(containerMaskGr);
        this.reelsContainerMask.position.set(this.maskDefaultXPos, this.maskDefaultYPos);
        this.reelsContainer.mask = this.reelsContainerMask;

        this.addChild(this.reelsContainerMask);
    }

    protected initExtra() {
        this.topStrip = new Sprite(Texture.from(`apocalypse/blocker-strip`));
        this.topStrip.name = `topStrip`;
        this.topStrip.scale.y = -1;
        this.topStrip.y = 15;
        this.addChild(this.topStrip);

        this.bottomStrip = new Sprite(Texture.from(`apocalypse/blocker-strip`));
        this.bottomStrip.name = `bottomStrip`;
        this.bottomStrip.y = 411;
        this.addChild(this.bottomStrip);

        this.fakeScatters = [];
        for (let i = 0; i < reelsConfig.reelsNumber; i++) {
            this.fakeScatters.push(new ApocalypseScatterSymbol());
        }
    }

    protected get maskDefaultXPos(): number {
        return -16;
    }

    protected get maskDefaultYPos(): number {
        return 0;
    }

    protected get maskDefaultWidth(): number {
        return 670;
    }

    protected get maskDefaultHeight(): number {
        return 428;
    }

    public onOrientationChange(): void {}

    public reset(): void {}
}
